import { useEffect, useMemo } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { fetchGraphData, fetchGraphDataHev } from "api/fetchLtool";
import { authTokenAtom } from "atoms/authAtoms";
import { stageConfigAtom } from "atoms/Ltool/stageConfigAtoms";
import { powertrainAtom } from "atoms/powertrainAtom";
import { gearIdAtom } from "atoms/gearAtom";
import { errorAtom } from "atoms/error";
import { allChartDataAtom, allChartLoadingAtom, ChartData } from "atoms/Ltool/chartAtoms";
import { createEmptyAllChartData } from "./initializer";
import { SelectedUnitId } from "atoms/Ltool/matrixAtoms";

type CellResult = { condition_id: string; data: ChartData["data"]; status: ChartData["status"] };

export function useAllChartDataSetter(
    unit_id: SelectedUnitId,
    conditionIds: string[]
) {
    const authToken = useAtomValue(authTokenAtom);
    const config = useAtomValue(stageConfigAtom);
    const powertrain = useAtomValue(powertrainAtom);
    const gearId = useAtomValue(gearIdAtom);
    const isHev = powertrain === "hev";
    const setAllChartData = useSetAtom(allChartDataAtom);
    const setLoading = useSetAtom(allChartLoadingAtom);
    const setError = useSetAtom(errorAtom);

    // conditionIds の内容を比較可能なキーに変換して依存配列に使用
    const conditionIdsKey = conditionIds.join("|");

    // join したキーから安定した配列を生成
    const normalizedConditionIds = useMemo(
        () => (conditionIdsKey ? conditionIdsKey.split("|") : []),
        [conditionIdsKey]
    );

    // ローディング状態の初期データをセット
    useEffect(() => {
        const initMap = createEmptyAllChartData(normalizedConditionIds);
        setAllChartData(initMap);
    }, [normalizedConditionIds, setAllChartData]);

    // APIから全条件のデータを取得
    useEffect(() => {
        if (!config.project_id || !authToken) return;
        // HEV は gear 単位で取得。gear 未選択時は呼ばない。
        if (isHev && !gearId) return;

        let cancelled = false;

        const fetchAllChartData = async () => {
            setLoading(true);
            setAllChartData(createEmptyAllChartData(normalizedConditionIds));
            try {
                const results = await Promise.all(
                    normalizedConditionIds.map(async (id): Promise<CellResult> => {
                        try {
                            const data = isHev
                                ? await fetchGraphDataHev(
                                    authToken,
                                    { ...config, condition_id: id },
                                    unit_id,
                                    gearId!
                                )
                                : await fetchGraphData(
                                    authToken,
                                    { ...config, condition_id: id },
                                    unit_id
                                );
                            return { condition_id: id, data, status: "success" };
                        } catch (err) {
                            console.error(`fetchGraphData failed for ${id}:`, err);
                            return {
                                condition_id: id,
                                data: {
                                    target: [],
                                    measurement: [],
                                    measurement_corrected: [],
                                    NG_freq: null,
                                    vel: [],
                                    rpm: [],
                                },
                                status: "error",
                            };
                        }
                    })
                );

                const resultMap = results.reduce<Record<string, ChartData>>(
                    (acc, { condition_id, data, status }) => {
                        acc[condition_id] = { data, status };
                        return acc;
                    },
                    {}
                );

                // 3) Atom を辞書で更新
                if (!cancelled) {
                    setAllChartData(resultMap);
                }
            } catch (unhandled) {
                if (!cancelled) {
                    setError(`Unexpected error in fetching all data: ${unhandled}`);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchAllChartData();

        return () => {
            cancelled = true;
            setLoading(false);
        };
    }, [authToken, config, unit_id, isHev, gearId, normalizedConditionIds, setAllChartData, setError, setLoading]);
};
